import { CompactButton, StandardButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { useCurrentUserSubscription } from '@/hooks/use-current-user-subscription';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Title } from '@mantine/core';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { IconHelpCircle } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useStripePromise } from './useStripePromise';

export const CheckoutSubscriptionPage = () => {
	const navigate = useNavigate();
	const { productPriceId } = useParams({ from: '/_app/checkout-subscription/$productPriceId' });
	const { loading, error, currentUserSubscription } = useCurrentUserSubscription();

	const helpButton = useMemo(
		() => (
			<CompactButton leftSection={<IconHelpCircle />} onClick={() => navigate({ to: '/help' })}>
				Help
			</CompactButton>
		),
		[navigate]
	);

	const goHomeButton = useMemo(
		() => <StandardButton onClick={() => navigate({ to: '/home' })}>Home</StandardButton>,
		[navigate]
	);

	return (
		<PrimaryLayout
			footer={{ children: goHomeButton }}
			header={{ backTo: '/choose-subscription', title: 'Subscriptions', children: helpButton }}
		>
			<ContentPanel>
				<ContentWithLoad
					loading={loading}
					error={!!error}
					isEmpty={!productPriceId}
					isEmptyMessage="No subscription selected"
					isErrorMessage="An Error occurred"
				>
					{currentUserSubscription?.id ? (
						<Flex direction="column" gap={50} align="center" justify="center" flex={1}>
							<Title order={2} ta="center">
								You are already subscribed to {currentUserSubscription?.name}
							</Title>
							<StandardButton onClick={() => navigate({ to: '/home' })} variant="light">
								Return Home
							</StandardButton>
						</Flex>
					) : (
						<Checkout />
					)}
				</ContentWithLoad>
			</ContentPanel>
		</PrimaryLayout>
	);
};

export const Checkout = () => {
	const { productPriceId } = useParams({ from: '/_app/checkout-subscription/$productPriceId' });
	const { stripePromise, clientSecret, loading, error } = useStripePromise(productPriceId);

	return (
		<ContentWithLoad
			loading={loading}
			error={!!error}
			isEmpty={!productPriceId}
			isEmptyMessage="No subscription selected"
			isErrorMessage="An Error occurred"
		>
			{!!stripePromise && !!clientSecret && (
				<EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
					<EmbeddedCheckout />
				</EmbeddedCheckoutProvider>
			)}
		</ContentWithLoad>
	);
};
